import { MEDIA_ID } from './media-ids.js';
/** Anything but `preparing` is `running`: every snapshot from before prefetch has no `phase`. */
export function phaseFrom(value) {
    return value === 'preparing' ? 'preparing' : 'running';
}
export const PREFETCH_DEFAULTS = {
    /** Requests at once per headset: 30 headsets share one access point (D7). */
    parallel: 1,
    /** Each headset waits a random 0 to this before its first request, so 30 don't open at once. */
    startSpreadMs: 10_000,
    /** A failed file is retried after these waits, then left for the lesson to load when it needs it. */
    retryMs: [2000, 8000, 30000],
    /** A headset reports progress at most this often. */
    reportEveryMs: 2000,
    /** Signed URLs are refreshed this long before they expire. */
    refreshBeforeExpiryMs: 60_000,
    /** A headset with less room than the plan's bytes plus this share reports `failed: storage` at once. */
    storageMargin: 0.2,
    /** A preparing session nobody starts ends this long after it was created. */
    preparingExpiryMinutes: 120,
    /** Above this many media bytes the console suggests preparing ahead (D12). */
    nudgeBytes: 50 * 1024 * 1024,
};
export const PREFETCH_STATES = ['fetching', 'ready', 'failed', 'paused'];
/** Why a headset gave up: no room, no network, or URLs the API no longer signs. */
export const PREFETCH_FAILURES = ['storage', 'network', 'signature'];
/** Far above any lesson; only there so a confused headset can't put silly numbers on the grid. */
const MAX_ITEMS = 10_000;
const MAX_BYTES = 2 ** 40;
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const fail = (error) => ({ ok: false, error });
const isCount = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const isState = (value) => typeof value === 'string' && PREFETCH_STATES.includes(value);
const isFailure = (value) => typeof value === 'string' && PREFETCH_FAILURES.includes(value);
/**
 * A headset's report, read leniently like `device.hello` (a headset in a classroom may run an older
 * or newer app): unknown keys are ignored, counts are rounded down and clamped, and a `failed` beside
 * a state that has not failed is dropped, a failure it cannot name reads as `network`, and `null`
 * reads as absent. Only a wrong type or an unknown state is refused. A `ready` headset has its first
 * step too, whatever the report says, so no tile can show "Klaar" beside "stap 1 niet klaar".
 */
export function parsePrefetchReport(payload) {
    if (!isRecord(payload))
        return fail('payload must be an object');
    const { itemsDone, itemsTotal, bytesDone, bytesTotal, firstStepReady, state, failed } = payload;
    const counts = { itemsDone, itemsTotal, bytesDone, bytesTotal };
    for (const [key, value] of Object.entries(counts)) {
        if (!isCount(value))
            return fail(`${key} must be a finite number, 0 or more`);
    }
    if (firstStepReady != null && typeof firstStepReady !== 'boolean') {
        return fail('firstStepReady must be true or false');
    }
    if (!isState(state))
        return fail(`state must be one of: ${PREFETCH_STATES.join(', ')}`);
    const items = Math.min(Math.floor(itemsTotal), MAX_ITEMS) + 0;
    const bytes = Math.min(Math.floor(bytesTotal), MAX_BYTES) + 0;
    const value = {
        // `+ 0` turns a -0 into 0.
        itemsDone: Math.min(Math.floor(itemsDone), items) + 0,
        itemsTotal: items,
        bytesDone: Math.min(Math.floor(bytesDone), bytes) + 0,
        bytesTotal: bytes,
        firstStepReady: state === 'ready' || firstStepReady === true,
        state,
    };
    // A failure the headset could not name is most likely the network.
    if (state === 'failed')
        value.failed = isFailure(failed) ? failed : 'network';
    return { ok: true, value };
}
/**
 * A headset's progress as a whole percent: from bytes when it knows them (one photo outweighs many
 * voice-overs), from items otherwise. Rounded down and kept at 99 until the headset says `ready`, so
 * "100 %" on the grid always means the headset is done.
 */
export function prefetchPercent(report) {
    if (report.state === 'ready')
        return 100;
    const { itemsDone, itemsTotal, bytesDone, bytesTotal } = report;
    const share = bytesTotal > 0 ? bytesDone / bytesTotal : itemsTotal > 0 ? itemsDone / itemsTotal : 0;
    return Math.max(0, Math.min(99, Math.floor(share * 100)));
}
export function deviceProgressOf(report) {
    const progress = {
        state: report.state,
        percent: prefetchPercent(report),
        ready: report.state === 'ready',
        firstStepReady: report.firstStepReady,
    };
    if (report.state === 'failed' && report.failed !== undefined)
        progress.failed = report.failed;
    return progress;
}
const SHA256 = /^[0-9a-f]{64}$/;
/**
 * `mediaInfo` read defensively: an entry the headset can't check a file against is dropped, not
 * fatal: that file is still fetched, only not checked, rather than the whole plan being lost.
 */
export function mediaInfoOf(value) {
    const info = {};
    if (!isRecord(value))
        return info;
    for (const [id, facts] of Object.entries(value)) {
        if (!MEDIA_ID.test(id) || !isRecord(facts))
            continue;
        const { bytes, sha256 } = facts;
        if (!Number.isSafeInteger(bytes) || bytes < 0)
            continue;
        if (typeof sha256 !== 'string' || !SHA256.test(sha256))
            continue;
        info[id] = { bytes: bytes, sha256 };
    }
    return info;
}
const PARALLEL_MAX = 2;
const START_SPREAD_MAX_MS = 60_000;
/**
 * The server's tunables, clamped: never more than two requests at once (D7: on one access point more
 * only makes everyone wait), and a start spread of at most a minute. Anything unreadable, including a
 * non-finite number, is the default rather than the bound: a server that sends Infinity is confused.
 */
export function tuningOf(value) {
    const { parallel, startSpreadMs } = PREFETCH_DEFAULTS;
    const tuning = { parallel, startSpreadMs };
    if (!isRecord(value))
        return tuning;
    const p = value['parallel'];
    if (typeof p === 'number' && Number.isFinite(p)) {
        tuning.parallel = Math.min(PARALLEL_MAX, Math.max(1, Math.floor(p)));
    }
    const s = value['startSpreadMs'];
    if (typeof s === 'number' && Number.isFinite(s)) {
        tuning.startSpreadMs = Math.min(START_SPREAD_MAX_MS, Math.max(0, Math.floor(s)));
    }
    return tuning;
}
//# sourceMappingURL=prefetch.js.map